import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams, useLocation } from 'react-router-dom'
import { useDispatch, connect } from 'react-redux'
import qs from 'qs'
import axios from 'axios'
import { sortBy } from 'lodash'
import Loader from '~components/Loader'
import { loadRangeProducts, loadSizeMap, clearRecentProducts, loadSize, setSizingMode } from '~redux/shop'
import { add as addRecentProduct } from '~redux/recentProducts'
import Heading from '~components/Heading'
import Swatch from '~components/ui/Swatch'
import MatrixOverlay from '~components/shop/MatrixOverlay'
import ProductMatrix from '~components/shop/ProductMatrix'
import Accordion from '~components/ui/Accordion'
import Button from '~components/ui/Button'
import ButtonLink from '~components/ui/ButtonLink'
import MediaQuery from '~components/helpers/MediaQuery'
import Gallery from '~components/Gallery'
import Legend from '~components/shop/Legend'
import Select from '~components/ui/Select'
import { pageView } from '~helpers/ga'
import InfoBubble from '~components/ui/InfoBubble'
import { getCurrency } from '~helpers/price'

const ShopStyle = ({ brands, products, rangeDetails, sizeMap, colourSwatches, stockist, sizingMode }) => {
  const [selectedColour, setSelectedColour] = useState(null)
  const [matrixOverlay, setMatrixOverlay] = useState(false)
  const [styleFilter] = useState('')
  const [braInstructions, setBraInstructions] = useState(null)
  const [briefInstructions, setBriefInstructions] = useState(null)
  const [downloads, setDownloads] = useState(null)

  const dispatch = useDispatch()
  const params = useParams()
  const location = useLocation()

  const rangeProducts = products[params.range]?.filter(product => product.custom_fields.style_code === params.style)
  const rangeProductDetails = rangeDetails[params.range]

  useEffect(() => {
    pageView()
    dispatch(loadSizeMap())
    dispatch(loadSize())

    dispatch(loadRangeProducts(params.range)).finally(() => {
      setDefaultColour()
      updateRecentProducts()
    })

    return () => {
      dispatch(clearRecentProducts())
    }
  }, [dispatch, params.range, params.style])

  useEffect(() => fetchCareInstructions(), [])
  useEffect(() => fetchMediaHubDownloads(), [selectedColour])

  const fetchCareInstructions = () => {
    axios.get('/options', { params: { option: 'care_instructions' } })
      .then(response => {
        setBraInstructions(response.data.fields.braInstructions)
        setBriefInstructions(response.data.fields.briefInstructions)
      })
  }

  const fetchMediaHubDownloads = () => {
    if (params.range && getColours().length > 0) {
      axios.get(`/api/media-hub/product/${params.range}`, {
        params: { colours: getColours().map(c => c.code) }
      })
        .then(response => setDownloads(response.data))
        .catch((e) => console.error('Failed to fetch media hub downloads:', e))
    }
  }

  const updateRecentProducts = () => {
    const product = rangeProducts?.find(product => product.custom_fields.colour_code === selectedColour)
    if (product) {
      dispatch(addRecentProduct(product.id))
    }
  }

  const setDefaultColour = () => {
    const colours = getColours()
    const query = qs.parse(location.search, { ignoreQueryPrefix: true })
    const defaultColour = colours.find(c => c.code === query.colour)?.code || colours[0]?.code
    setSelectedColour(defaultColour)
  }

  const getColours = () => {
    return rangeProducts?.reduce((carry, product) => {
      const colour = product.custom_fields.colour_code
      const colourName = product.custom_fields.colour
      if (!carry.find(c => c.code === colour)) {
        carry.push({ code: colour, name: colourName })
      }
      return carry
    }, []) || []
  }

  const getBrand = () => {
    const brandId = rangeProducts?.[0]?.brand_id
    return brands.find(b => b.id === brandId)
  }

  const handleColourChange = (colour) => {
    setSelectedColour(colour)
  }

  const handleSizingModeChange = (e) => {
    const { value } = e.target
    axios.post('/api/account/store-size', { size: value })
    dispatch(setSizingMode(value))
  }

  const toggleMatrixOverlay = () => {
    setMatrixOverlay(!matrixOverlay)
    document.body.classList.toggle('overflow-y-hidden')
  }

  if (!rangeProducts || !sizeMap || !brands) {
    return <Loader />
  }

  const styles = rangeProducts.reduce((carry, product) => {
    if (!carry.find(i => i.name === product.custom_fields.style)) {
      carry.push({
        name: product.custom_fields.style,
        value: product.custom_fields.style
      })
    }

    return carry
  }, [])

  styles.sort()

  const styleProducts = rangeProducts.filter(product => {
    if (styleFilter && product.custom_fields.style !== styleFilter) {
      return false
    }

    return product.custom_fields.colour_code === selectedColour
  })

  const sizingOptions = Object.keys(sizeMap.bra.bands[0]).map(s => ({
    name: s.toUpperCase().replace(/GB/g, 'UK').replace(/DE/g, 'EU'),
    value: s
  }))

  let headingPrices = null

  if (styleProducts.length > 0) {
    const currency = getCurrency(stockist.country)

    headingPrices = [
      {
        label: 'Trade',
        value: rangeProducts[0].trade_price,
        currency
      }
    ]
  }

  const breadcrumbs = [{
    title: 'Shop',
    url: '/shop'
  },
  {
    title: getBrand()?.name,
    url: `/shop/brands/${getBrand()?.slug}`
  },
  {
    title: rangeProductDetails ? rangeProductDetails.name : params.range,
    url: `/shop/ranges/${params.range}?colour=${selectedColour}`
  }]

  const colours = getColours()

  let headingTitle = rangeProducts[0]?.name

  if (!headingTitle) {
    headingTitle = 'Not Found'
  }

  return (
    <>
      <Heading
        breadcrumbs={breadcrumbs}
        title={headingTitle}
        price={headingPrices}
        className="mt-2 lg:mt-10"
      />

      <div className="mt-4 md:mt-6">
        <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
          <div className="flex-wrap items-center justify-between hidden -mx-2 -mb-2 lg:flex lg:-mx-3 lg:mt-6 xl:mt-8">
            <Legend />
          </div>
        </div>
      </div>

      <MediaQuery type="max" breakpoint="lg">
        <div className="mt-4 md:mt-6 lg:hidden">
          <div className="px-4 mx-auto xxl:container md:px-8 xxl:px-0">
            <div className="flex items-center w-full">
              <div>
                <span className="block mb-2">
                  Colours:
                </span>

                <div className="flex flex-wrap items-center -m-1">
                  {getColours().map(colour => (
                    <Swatch
                      key={colour.code}
                      colour={colour.code}
                      name={colour.name}
                      swatch={colourSwatches[colour.code]}
                      checked={selectedColour === colour.code}
                      onChange={handleColourChange.bind(this, colour.code)}
                      className="px-1 my-1"
                    />
                  ))}
                </div>
              </div>

              <div className="ml-auto">
                <Select
                  items={sizingOptions}
                  value={sizingMode}
                  onChange={handleSizingModeChange.bind(this)}
                />
              </div>
            </div>
          </div>
        </div>
      </MediaQuery>

      <div className="w-full mt-8 mb-20">
        <div className="w-full px-4 mx-auto xxl:container md:px-8 xxl:px-0">
          {rangeProducts.map((product) => {
            const images = sortBy(product.images, 'sort_order')

            const accordions = []

            if (product.description) {
              accordions.push({
                title: 'Product information',
                content: product.description
              })
            }

            if (product.is_bra && braInstructions) {
              accordions.push({
                title: 'Care Instructions',
                content: braInstructions
              })
            } else if (briefInstructions) {
              accordions.push({
                title: 'Care Instructions',
                content: briefInstructions
              })
            }

            return (
              <div className="flex flex-wrap -mx-2 -my-5 lg:-mx-3 xl:-mx-5" key={product.id}>
                <div className="w-full px-2 my-5 lg:px-3 xl:px-5 lg:w-3/12">
                  <Gallery
                    title={product.name}
                    images={images}
                  />

                  <MediaQuery breakpoint="lg">
                    <div className="w-full mt-6 -mb-4 xl:mt-8 xl:-mb-6">
                      {downloads && downloads[product.custom_fields.colour_code] && downloads[product.custom_fields.colour_code].map((download) => (
                        <div
                          key={download.url}
                          className="flex items-center justify-between w-full mb-4 xl:mb-6"
                        >
                          <ButtonLink
                            href={download.url}
                            theme="secondary"
                            className="flex-1"
                          >
                            {download.label}
                          </ButtonLink>

                          <InfoBubble
                            className="ml-2 w-7 h-7 group"
                            tooltip={download.tooltip}
                          />
                        </div>
                      ))}
                    </div>
                  </MediaQuery>
                </div>

                <div className="w-full px-2 my-5 lg:px-3 lg:w-9/12 xl:px-5">
                  <span className="block text-sm lg:text-md">
                    Style #
                    {product.custom_fields.style_code}
                  </span>

                  <MediaQuery type="max" breakpoint="lg">
                    <Button className="w-full mt-4" onClick={() => toggleMatrixOverlay()}>
                      Select sizes
                    </Button>
                  </MediaQuery>

                  <div className="mt-6 editor editor-standard">
                    {product.short_description}
                  </div>

                  <MediaQuery type="max" breakpoint="lg">
                    <div className="mt-10">
                      <div className="flex flex-wrap -m-2">
                        {downloads && downloads[product.custom_fields.colour_code] && downloads[product.custom_fields.colour_code].map((download, index) => (
                          <div key={download.url} className="flex items-center justify-between w-full px-2 my-2 sm:w-1/2">
                            <ButtonLink href={download.url} theme="secondary" className="w-full">
                              {download.label}
                            </ButtonLink>

                            <InfoBubble
                              className="ml-2 w-7 h-7 group"
                              tooltip={download.tooltip}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </MediaQuery>

                  <MediaQuery breakpoint="lg">
                    <div className="items-center justify-between hidden w-full mt-10 lg:flex">
                      <div className="flex items-center">
                        <span className="block mr-4 xl:mr-6">
                          Colours:
                        </span>

                        <div className="flex items-center -m-1">
                          {getColours().map(colour => (
                            <Swatch
                              key={colour.code}
                              colour={colour.code}
                              name={colour.name}
                              swatch={colourSwatches[colour.code]}
                              checked={selectedColour === colour.code}
                              onChange={handleColourChange.bind(this, colour.code)}
                              className="px-1 my-1"
                            />
                          ))}
                        </div>
                      </div>

                      <Select
                        items={sizingOptions}
                        value={sizingMode}
                        onChange={handleSizingModeChange.bind(this)}
                      />
                    </div>
                  </MediaQuery>

                  <div className="hidden mt-12 overflow-x-auto lg:block">
                    <ProductMatrix key={product.id} product={product} />
                  </div>

                  <div className="w-full mt-9 lg:15 xl:mt-20">
                    <Accordion items={accordions} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {rangeProducts.map((product, index) => (
        index === 0 && (
          <MatrixOverlay
            key={index}
            product={rangeProducts[0]}
            colours={colours}
            overlay={matrixOverlay}
            toggleOverlay={() => toggleMatrixOverlay()}
          />
        )
      ))}
    </>
  )
}

ShopStyle.propTypes = {
  products: PropTypes.object,
  brands: PropTypes.array,
  colourSwatches: PropTypes.object,
  sizeMap: PropTypes.object,
  sizingMode: PropTypes.string,
  rangeDetails: PropTypes.object,
  stockist: PropTypes.object
}

const mapStateToProps = (state, ownProps) => ({
  ...ownProps,
  brands: state.shop.brands,
  products: state.shop.products,
  rangeDetails: state.shop.rangeDetails,
  colourSwatches: state.shop.colourSwatches,
  sizeMap: state.shop.sizeMap,
  sizingMode: state.shop.sizingMode,
  stockist: state.auth.account.stockist
})

export default connect(mapStateToProps)(ShopStyle)
